import { useState } from "react";
import { runEval } from "../../utils/evaluation-functions";
import { environment } from "../../config/environment";

const MODEL_OPTIONS = [
  { value: "openai:gpt-4o", label: "GPT 4o (openai:gpt-4o)" },
  { value: "openai:gpt-5", label: "GPT 5 (openai:gpt-5)" },
  {
    value: "anthropic:claude-sonnet-4-6",
    label: "Claude Sonnet 4.6 (anthropic:claude-sonnet-4-6)",
  },
  {
    value: "anthropic:claude-sonnet-4-5-20250929",
    label: "Claude Sonnet 4.5 (anthropic:claude-sonnet-4-5-20250929)",
  },
  {
    value: "anthropic:claude-3-7-sonnet-20250219",
    label: "Claude 3.7 Sonnet (anthropic:claude-3-7-sonnet-20250219)",
  },
  {
    value: "anthropic:claude-3-5-sonnet-20241022",
    label: "Claude 3.5 Sonnet (anthropic:claude-3-5-sonnet-20241022)",
  },
  { value: "knowtex:mock", label: "Knowtex Mock (knowtex:mock)" },
];

export default function RunEvalModal({ datasetName, closeModal }) {
  const [model, setModel] = useState("openai:gpt-4o");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!model) {
      setError("Please select a model.");
      return;
    }
    setIsSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const resp = await runEval(datasetName, { model });
      setResult(resp);
    } catch (err) {
      setError(err?.message || "Failed to start eval run.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-ktx-purple text-white px-6 py-4 rounded-t-lg flex justify-between items-center">
          <h2 className="text-xl font-semibold">Run Eval: {datasetName}</h2>
          <button
            type="button"
            onClick={closeModal}
            className="text-white hover:text-gray-200 focus:outline-none"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            <div>
              <label
                htmlFor="model"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Model
              </label>
              <select
                id="model"
                name="model"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                disabled={isSubmitting || !!result}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple sm:text-sm"
              >
                {MODEL_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                The selected model overrides the workflow-decided summarization
                model in <code>RunChatgptFunction</code> for this eval run.
              </p>
            </div>

            <div className="bg-blue-50 rounded-md p-4 border border-blue-200">
              <p className="text-sm text-blue-700">
                One SQS message will be queued per row in the dataset. Results
                land in the <code>Eval-SummarizeJobs-&lt;env&gt;</code> table
                only, with no writes to production Postgres, SNS, or downstream
                SQS queues.
              </p>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4 border border-red-200">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {result && (
              <div className="rounded-md bg-green-50 p-4 border border-green-200 text-sm text-green-800 space-y-1">
                <p>
                  <span className="font-semibold">Run ID:</span>{" "}
                  <code>{result.run_id}</code>
                </p>
                <p>
                  <span className="font-semibold">Queued:</span> {result.queued}
                  {" / "}
                  {result.total_rows} rows
                  {result.failed ? ` (failed: ${result.failed})` : ""}
                  {result.skipped ? ` (skipped: ${result.skipped})` : ""}
                </p>
                <p>
                  <span className="font-semibold">Model:</span> {result.model}
                </p>
                <p className="text-xs text-green-700">
                  Look for rows in <code>{environment.evalSummarizeJobsTable}</code> with{" "}
                  <code>eval_run_id = {result.run_id}</code>.
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ktx-purple"
                disabled={isSubmitting}
              >
                {result ? "Close" : "Cancel"}
              </button>
              {!result && (
                <button
                  type="submit"
                  disabled={isSubmitting || !model}
                  className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ktx-purple ${
                    isSubmitting || !model
                      ? "bg-ktx-purple-light cursor-not-allowed"
                      : "bg-ktx-purple hover:bg-ktx-purple-dark"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Queueing...
                    </>
                  ) : (
                    "Run Eval"
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
